import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import AppContext from '../../../../contexts/AppContext';
import ObservationsList from './ObservationsList';
import ObservationDeleteModal from './ObservationDeleteModal';
import { toast } from 'react-toastify';
import commentableApi from '../../../../utils/api/commentable';
import { FormContext } from '@stormwater-software/lib-form';
import { get } from 'lodash';
import { InspectionContext } from '../../../../contexts/InspectionContext';

export const getUpdatedNoteMapper = (updatedNote) => (note) => {
  if (note.id === updatedNote.id) {
    return Object.assign({}, note, {
      content: updatedNote.content,
      observation_type: updatedNote.observation_type,
      // SW2-2472: an edited inspection note comes back as `{ note: { content } }`; without this
      // the list kept showing the text from before the edit.
      ...(updatedNote.note ? { note: updatedNote.note } : {}),
    });
  }

  return note;
};

export const getDeletedNoteFilter = (deletedNoteId) => (note) => {
  return note.id !== deletedNoteId;
};

function ObservationsFormControl({ value, className, ...props }) {
  if (!Array.isArray(value)) {
    return null;
  }

  const formContext = useContext(FormContext);
  const appContext = useContext(AppContext);
  const inspectionContext = useContext(InspectionContext);

  const [showDelete, setShowDelete] = useState(false);
  const [selected, setSelected] = useState(null);

  const onSuccess = () => {
    appContext.triggerSuccessNotification(true);
  };

  /**
   * SW2-2321: neither of these chains had a `.catch`, so a rejection became an
   * uncaught runtime error — a red overlay in dev, a silent no-op in
   * production. That is reachable in normal use: observations are commentables
   * on `findings`, and only `inspections` commentables have an offline cache,
   * so editing or deleting one while offline always rejects. Tell the user
   * instead of crashing.
   */
  const onWriteFailed = (verb) => (err) => {
    if (err && err.offlineUnsupported) {
      toast.error(
        `Observations cannot be ${verb} while offline. Reconnect and try again.`,
      );

      return;
    }

    console.error(`Failed to ${verb.replace(/ed$/, '')} observation`, err);
    toast.error(`Failed to ${verb.replace(/ed$/, '')} observation.`);
  };

  const onEdit = (data) =>
    commentableApi
      .update('findings', formContext.value.id, data)
      .then((updatedNote) => {
        const mapNotes = getUpdatedNoteMapper(updatedNote);

        formContext.set(
          'observations',
          get(formContext.value, 'observations', []).map(mapNotes),
        );

        inspectionContext.setFindings(
          inspectionContext.findings.map((finding) => {
            if (finding.id === formContext.value.id) {
              return Object.assign({}, finding, {
                commentables: finding.commentables.map(mapNotes),
              });
            }

            return finding;
          }),
        );
      })
      .then(onSuccess)
      .catch(onWriteFailed('edited'));

  const onDelete = () =>
    commentableApi
      .destroy(selected.id, 'findings', formContext.value.id)
      .then(() => {
        const filterNotes = getDeletedNoteFilter(selected.id);

        formContext.set(
          'observations',
          get(formContext.value, 'observations', []).filter(filterNotes),
        );

        inspectionContext.setFindings(
          inspectionContext.findings.map((finding) => {
            if (finding.id === formContext.value.id && finding.commentables) {
              return Object.assign({}, finding, {
                commentables: finding.commentables.filter(filterNotes),
              });
            }

            return finding;
          }),
        );
      })
      .then(onSuccess)
      .catch(onWriteFailed('deleted'));

  useEffect(() => {
    if (!selected && showDelete) {
      setShowDelete(false);
    }
  }, [selected, showDelete]);
    
  return (
    <div className={classNames("form-control--no-theme", className)}>
      <ObservationsList
        data={value}
        onEdit={() => {}}
        onSave={onEdit}
        onDelete={(data) => {
          setShowDelete(true);
          setSelected(data);
        }}
        {...props}
      />
      <ObservationDeleteModal
        show={showDelete}
        value={selected}
        onCancel={() => setSelected(null)}
        onSubmit={onDelete}
      />
    </div>
  );
}

ObservationsFormControl.propTypes = {
  value: PropTypes.any,
  canEdit: PropTypes.bool.isRequired,
  canDelete: PropTypes.bool.isRequired,
};
ObservationsFormControl.defaultProps = {
  value: [],
  canEdit: false,
  canDelete: false,
};

export default ObservationsFormControl;
